/**
 * "What's new" (go-live, 25 Oct 2026; design: docs/product/WHATS_NEW_DESIGN_BRIEF.md + the
 * "learnbee-whats-new-handoff" package). Shown once, over the app, to an existing user whose own
 * work was moved into their new organisation on this sign-in — instead of the setup screen, which is
 * for someone starting from nothing. Top: what's new. Bottom: their work (counts as hexagons) or,
 * with nothing moved, "Your organisation is ready". An optional inline rename. One layout on every
 * screen (24 Oct 2026, Jasmine: no separate phone version) — narrower screens stack it and scroll.
 *
 * Built on Base UI's dialog (as src/components/ui/dialog.tsx is) for the focus trap, Esc and
 * labelling, with the design's own scrim and layout. The parent decides when it shows and what
 * each exit does.
 */
import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

export type MovedCounts = { courses: number; images: number; videos: number; pdfs: number }

export interface WhatsNewDialogProps {
  open: boolean
  firstName?: string | null
  orgName: string
  counts: MovedCounts
  /** Save a new organisation name. Rejects to show the error state. */
  onRename: (name: string) => Promise<void>
  /** Each exit marks the pop-up seen, then goes on. */
  onGetStarted: (variant: 'with-work' | 'no-courses') => void
  onInvite: () => void
  onClose: () => void
}

const fmt = (n: number) => n.toLocaleString('en-GB')

const COUNT_TYPES = [
  { key: 'courses', one: 'course', many: 'courses' },
  { key: 'images', one: 'image', many: 'images' },
  { key: 'videos', one: 'video', many: 'videos' },
  { key: 'pdfs', one: 'PDF', many: 'PDFs' },
] as const

/** "Pratibha Singh's organisation" → PS; "Singh Learning Studio" → SL; "My organisation" → MO. */
function orgInitials(name: string) {
  return name.replace(/['’]s organisation$/i, '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('')
}

const HEX = 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)'

const FEATURES: Array<{ title: string; text: string; icon: ReactNode }> = [
  {
    title: 'Invite your team',
    text: 'Add Admins, Creators and Learners by email, CSV or an organisation code.',
    icon: <path d="M18.5 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.5-.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM13 29c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5m.8-4.6c.4-.1.8-.2 1.2-.2 2.3 0 4.1 1.6 4.6 4.3" />,
  },
  {
    title: 'Launch courses',
    text: 'Send courses to people or groups, with due dates and reminders.',
    icon: <path d="m13.5 21.5 16-7-6 16-3-6.5-7-2.5Zm7 2.5 3.5-3.5" />,
  },
  {
    title: 'See reports',
    text: 'Track progress and see how every learner is doing.',
    icon: <path d="M15 29v-5m5 5v-9m5 9v-12m5 12v-7M13 29.5h18" />,
  },
]

function HexIcon({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden="true" className="relative block size-11 text-[#7A3BD9] md:size-[52px]">
      <svg viewBox="0 0 44 44" fill="none" className="absolute inset-0 size-full">
        <path d="M22 2.5 39.3 12.5v19L22 41.5 4.7 31.5v-19Z" fill="#fff" stroke="#DFCDFB" strokeWidth="1.3" strokeLinejoin="round" />
      </svg>
      <svg viewBox="0 0 44 44" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="absolute inset-0 size-full">
        {children}
      </svg>
    </span>
  )
}

const Tick = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m4 10.5 4 4 8-9" />
  </svg>
)

type NameUi = 'view' | 'edit' | 'saving' | 'saved' | 'error'

export function WhatsNewDialog(props: WhatsNewDialogProps) {
  const { open, firstName, counts, onRename, onGetStarted, onInvite, onClose } = props
  const [orgName, setOrgName] = useState(props.orgName)
  const [nameUi, setNameUi] = useState<NameUi>('view')
  const [draft, setDraft] = useState(props.orgName)
  const inputRef = useRef<HTMLInputElement>(null)
  const primaryRef = useRef<HTMLButtonElement>(null)

  const moved = COUNT_TYPES.map(t => ({ ...t, n: counts[t.key] })).filter(t => t.n > 0)
  const variant: 'with-work' | 'no-courses' = moved.length > 0 ? 'with-work' : 'no-courses'
  const editing = nameUi === 'edit' || nameUi === 'saving' || nameUi === 'error'

  useEffect(() => { if (editing) inputRef.current?.focus() }, [editing])

  async function save() {
    const value = draft.trim()
    if (!value || value === orgName) return setNameUi('view')
    setNameUi('saving')
    try { await onRename(value); setOrgName(value); setNameUi('saved') }
    catch { setNameUi('error') }
  }

  const renameLink = nameUi === 'view' ? (
    <button type="button" onClick={() => { setDraft(orgName); setNameUi('edit') }}
      className="ml-1 rounded font-semibold text-[#5B21B6] underline decoration-[1.5px] underline-offset-[3px]">
      Rename<span className="sr-only"> organisation</span>
    </button>
  ) : nameUi === 'saved' ? (
    <span role="status" className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-[#E8F5EE] px-2 py-0.5 align-[1px] text-[13.5px] font-medium text-[#1E7A4C]">
      <Tick size={13} />Name saved
    </span>
  ) : null

  return (
    // Esc / scrim close the pop-up — but not while the name is being edited (Esc cancels the edit).
    <DialogPrimitive.Root open={open} onOpenChange={o => { if (!o && !editing) onClose() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-[80] bg-[rgba(30,20,52,.48)] backdrop-blur-[2px]" />
        <DialogPrimitive.Popup initialFocus={primaryRef}
          className="fixed left-1/2 top-1/2 z-[81] flex max-h-[calc(100dvh-32px)] w-[calc(100%-32px)] max-w-[1000px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl bg-white shadow-[0_30px_80px_-20px_rgba(30,20,52,.45)] outline-none md:max-h-[calc(100dvh-48px)] md:w-[calc(100%-48px)]">
          <button type="button" aria-label="Close" onClick={onClose}
            className="absolute right-4 top-4 z-10 grid size-10 place-items-center rounded-xl bg-white/70 text-[#24202E] hover:bg-white">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M5 5l10 10M15 5 5 15" />
            </svg>
          </button>

          <div className="min-h-0 flex-1 overflow-auto">
            {/* Top: what's new */}
            <section className="bg-[#F7F2FE] px-5 pb-6 pt-7 md:px-14 md:pb-10 md:pt-11">
              <p className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-[#DFCDFB] bg-[#EFE5FD] py-1 pl-[9px] pr-3 text-[13.5px] font-semibold text-[#5B21B6]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-[#7A3BD9]" aria-hidden="true">
                  <path d="M12 2.5l2.2 6.3 6.3 2.2-6.3 2.2L12 19.5l-2.2-6.3L3.5 11l6.3-2.2Z" />
                </svg>
                What’s new
              </p>
              <p className="mb-2 text-[15.5px] font-medium text-[#5B21B6] md:text-[17px]">{firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}</p>
              <DialogPrimitive.Title className="pr-12 text-[27px] font-semibold leading-[1.2] tracking-[-0.02em] text-[#24202E] md:text-[38px] md:leading-[1.15]">
                Learnbee is now built for teams.
              </DialogPrimitive.Title>
              <ul className="mt-6 grid gap-[18px] md:mt-7 md:grid-cols-3 md:gap-10">
                {FEATURES.map(f => (
                  <li key={f.title} className="grid grid-cols-[44px_1fr] gap-3.5 md:grid-cols-1 md:gap-3">
                    <HexIcon>{f.icon}</HexIcon>
                    <div>
                      <h3 className="text-base font-semibold leading-snug text-[#24202E] md:text-[17px]">{f.title}</h3>
                      <p className="mt-0.5 max-w-[30ch] text-[14.5px] text-[#4A4656] md:text-[15px]">{f.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* Bottom: their work */}
            <section aria-labelledby="wn-work"
              className="grid gap-3.5 border-t border-[#EFE5FD] px-5 py-6 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center md:gap-x-9 md:gap-y-0 md:px-14 md:pb-6 md:pt-7">
              {variant === 'with-work' ? (
                <ul aria-label="Moved into your organisation" className="flex gap-1 self-start pb-[22px]">
                  {moved.map((t, i) => (
                    <li key={t.key} className={`relative grid h-[72px] w-16 place-content-center text-center md:h-[79px] md:w-[70px] ${i % 2 ? 'translate-y-[22px]' : ''}`}>
                      <span aria-hidden="true" className="absolute inset-0 bg-[#DFCDFB]" style={{ clipPath: HEX }} />
                      <span aria-hidden="true" className={`absolute inset-[1.5px] ${t.key === 'courses' ? 'bg-[#EFE5FD]' : 'bg-white'}`} style={{ clipPath: HEX }} />
                      <span className="relative text-[17px] font-semibold leading-none tracking-[-0.02em] text-[#5B21B6] md:text-[19px]">{fmt(t.n)}</span>
                      <span className="relative mt-1 text-[11.5px] text-[#6B6678]">{t.n === 1 ? t.one : t.many}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span aria-hidden="true" className="relative grid h-[86px] w-[76px] place-items-center">
                  <span className="absolute inset-0 bg-[#DFCDFB]" style={{ clipPath: HEX }} />
                  <span className="absolute inset-[1.5px] bg-[#EFE5FD]" style={{ clipPath: HEX }} />
                  <span className="relative text-2xl font-semibold tracking-[.02em] text-[#5B21B6]">{orgInitials(orgName)}</span>
                </span>
              )}

              <div>
                <h2 id="wn-work" className="flex items-center gap-2 text-lg font-semibold text-[#24202E]">
                  <span className="text-[#1E7A4C]"><Tick /></span>
                  {variant === 'with-work' ? 'Everything you made is here' : 'Your organisation is ready'}
                </h2>
                <p className="mt-1 max-w-[42ch] text-[15.5px] text-[#4A4656]">
                  {variant === 'with-work'
                    ? <>All in <strong className="font-semibold text-[#24202E]">{orgName}</strong>, private to you until you share it.</>
                    : <><strong className="font-semibold text-[#24202E]">{orgName}</strong> is set up for you.</>}
                  {renameLink}
                </p>

                {editing && (
                  <div className="mt-2.5 max-w-[520px]">
                    <label htmlFor="wn-org" className="text-[13.5px] font-semibold text-[#24202E]">Organisation name</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      <input id="wn-org" ref={inputRef} value={draft} maxLength={120} readOnly={nameUi === 'saving'}
                        aria-invalid={nameUi === 'error' || undefined} aria-describedby={nameUi === 'error' ? 'wn-org-err' : 'wn-org-help'}
                        onChange={e => setDraft(e.target.value)} onFocus={e => e.currentTarget.select()}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); void save() }
                          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setNameUi('view') }
                        }}
                        autoComplete="organization"
                        className="h-[42px] min-w-0 flex-[1_1_220px] rounded-[10px] border-[1.5px] border-[#7A3BD9] bg-white px-3 text-base text-[#24202E] outline-none read-only:border-[#E7E2EF] read-only:text-[#6B6678] aria-[invalid]:border-[#B42318]" />
                      <div className="flex gap-2 max-md:w-full">
                        <button type="button" onClick={() => void save()} disabled={nameUi === 'saving'}
                          className="inline-flex h-[42px] items-center justify-center gap-2 rounded-[10px] bg-[#7A3BD9] px-4 text-[15px] font-semibold text-white hover:bg-[#9333EA] disabled:cursor-progress disabled:opacity-75 max-md:flex-1">
                          {nameUi === 'saving' && <span aria-hidden="true" className="size-[15px] animate-spin rounded-full border-2 border-current border-r-transparent" />}
                          {nameUi === 'saving' ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" onClick={() => setNameUi('view')} disabled={nameUi === 'saving'}
                          className="h-[42px] rounded-[10px] border-[1.5px] border-[#DFCDFB] bg-white px-4 text-[15px] font-semibold text-[#5B21B6] hover:bg-[#F7F2FE] disabled:opacity-75 max-md:flex-1">
                          Cancel
                        </button>
                      </div>
                    </div>
                    {nameUi === 'error'
                      ? <p id="wn-org-err" role="alert" className="mt-2 rounded-[9px] bg-[#FEF1F0] px-3 py-2 text-[13.5px] text-[#B42318]">Couldn’t save the name. Try again, or keep going and rename it later in Settings.</p>
                      : <p id="wn-org-help" className="mt-1.5 text-[13px] text-[#6B6678]">Optional. You can rename it any time in Settings.</p>}
                  </div>
                )}
              </div>

              {/* The actions: beside the text on wide screens, under it on narrow ones. */}
              <div className="flex flex-col gap-2.5 md:min-w-[220px]">
                <Actions primaryRef={primaryRef} onGetStarted={() => onGetStarted(variant)} onInvite={onInvite} />
              </div>

              {variant === 'with-work' && (
                <p className="mt-1 flex items-start gap-2 border-t border-[#E7E2EF] pt-4 text-[13px] text-[#6B6678] md:col-span-3 md:mt-5 md:items-center md:text-[13.5px]">
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#7A3BD9" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 flex-none md:mt-0">
                    <path d="M8.5 11.5a3.5 3.5 0 0 0 5 0l2.5-2.5a3.5 3.5 0 0 0-5-5L10 5m1.5 3.5a3.5 3.5 0 0 0-5 0L4 11a3.5 3.5 0 0 0 5 5l1-1" />
                  </svg>
                  Published course links and SCORM packages in your LMS keep working.
                </p>
              )}
            </section>
          </div>

        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function Actions({ primaryRef, onGetStarted, onInvite }: {
  primaryRef?: Ref<HTMLButtonElement>; onGetStarted: () => void; onInvite: () => void
}) {
  return (
    <>
      <button ref={primaryRef} type="button" onClick={onGetStarted}
        className="inline-flex h-12 items-center justify-center rounded-xl bg-[#7A3BD9] px-[22px] text-base font-semibold text-white hover:bg-[#9333EA]">
        Get started
      </button>
      <button type="button" onClick={onInvite}
        className="inline-flex h-12 items-center justify-center rounded-xl border-[1.5px] border-[#DFCDFB] bg-white px-[22px] text-base font-semibold text-[#5B21B6] hover:bg-[#F7F2FE]">
        Invite your team
      </button>
    </>
  )
}
